import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Form, Grid, Dropdown } from 'semantic-ui-react';
import styled from 'styled-components';
import moment from 'moment';
import { DateForm, DateType, Pagination, SearchInputBox, SikdaeGroup, TableForm, LoadingBar } from 'components/Commons';
import * as companyActions from 'actions/commons';
import * as usageAction from 'actions/usage';
import cm from 'helpers/commons';
import UserSikdaeUsageDetailModal from '../components/UserSikdaeUsageDetailModal';
import exportedJsonData from '../components/UserSikdaeUsageDetailModal/usageHisoryJsonData.json';
import toastr from '../helpers/toastr';

const InputWrapper = styled.div`
  & .search-input button {
    display: none;
  }
`;

const dateTypeLabel = { DD: '일 단위', MM: '월 단위', SUM: '합계' };
const defaultColumns = [
  { header: '날짜', name: 'usingDate', textAlign: 'left' },
  { header: '로그인 ID', name: 'signId', textAlign: 'left' },
  { header: '이름', name: 'name', textAlign: 'left' },
  { header: '사번', name: 'comIdNum', textAlign: 'left' },
  { header: '휴대전화번호', name: 'cellphone', textAlign: 'left' },
  { header: '이메일', name: 'email', textAlign: 'left' },
  { header: '직위', name: 'rankPosition', textAlign: 'left' },
  { header: '직책', name: 'position', textAlign: 'left' },
  { header: '식대그룹', name: 'mealGroupName', textAlign: 'left' },
  { header: '총 식사횟수', name: 'mealCount', textAlign: 'right' },
  { header: '식대사용 금액', name: 'comPointSum', textAlign: 'right' }
];
const pageSizeOptions = [
  { text: '10씩 보기', value: 10 },
  { text: '25씩 보기', value: 25 },
  { text: '50씩 보기', value: 50 },
  { text: '100씩 보기', value: 100 },
  { text: '200씩 보기', value: 200 }
];

const generateColumns = (values) => {
  const columns = cm.tableHeaderList(defaultColumns, 6, null, 'notMaxWidth');
  const thead = columns.map((item) => ({ data: item.header, ...item }));
  const tbody = values.map((value) => {
    const division = cm.trTdDivision(value.orgCode);
    const row = columns.map((item) => {
      let data = value[item.name] || '-';
      if (item.header.includes('부서')) {
        const idx = Number(item.header.replace('부서', '')) - 1;
        data = division[idx] || '-';
      }
      if (item.name === 'mealCount' || item.name === 'comPointSum') {
        data = (value[item.name] || 0).toLocaleString();
      }
      return { data, ...item };
    });
    return { row };
  });
  return { thead, tbody: values.length ? tbody : undefined };
};

const UserSikdaeUsageHistory = ({
  companyAPI,
  usageAPI,
  date,
  dateType,
  group,
  keyword,
  userSikdaeUsageHistory,
  userSikdaeUsageTotal
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchDate, setSearchDate] = useState({ startDate: '-', endDate: '-' });
  const [isOpen, setIsOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isDisable, setIsDisable] = useState(false);

  useEffect(() => {
    usageAPI.cleanUserSikdaeUsage();
    companyAPI.sikdaeGroupList();
  }, []);

  const { usageHistory, usageTotalHistory, usageTotal } = useMemo(() => {
    const { totalRow = 0, content = [] } = userSikdaeUsageHistory.data || {};
    const { totalMealCount, totalComPoint } = userSikdaeUsageTotal.data || {};
    const histories = content.slice((page - 1) * pageSize, page * pageSize);
    return {
      usageHistory: histories,
      usageTotalHistory: content,
      usageTotal: { totalMealCount, totalComPoint, totalRow }
    };
  }, [page, pageSize, userSikdaeUsageHistory, userSikdaeUsageTotal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const params = {
        startDate: date
          ? moment(date.sDate).format('YYYY-MM-DD')
          : moment()
              .startOf('month')
              .format('YYYY-MM-DD'),
        endDate: date ? moment(date.eDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        groupingCondition: dateType.value || 'MM',
        mealGroupIdx: group.value || null,
        keyword: keyword.value || null
      };
      if (moment(params.endDate).diff(params.startDate, 'day') >= 62) {
        alert(`날짜 검색 조건 최대 범위는 62일까지 입니다.`);
        return;
      }
      await usageAPI.getUserSikdaeUsageHistory(params);
      await usageAPI.getUserSikdaeUsageTotal(params);
    } catch (e) {
      setIsLoading(false);
      const { response } = e;
      if (response && response.data) {
        toastr('top-right', 'error', response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnOffModal = () => setIsOpen(!isOpen);
  const handleRowClick = (_, index) => {
    const { userId, usingDate } = usageHistory[index] || {};
    if (userId) {
      const [startDate, endDate] = usingDate.split(' ~ '); // SUM
      const unitOfTime = dateType.value === 'MM' ? 'month' : 'day';

      const params = {
        startDate: moment(startDate || usingDate)
          .startOf(unitOfTime)
          .format('YYYY-MM-DD'),
        endDate: moment(endDate || usingDate)
          .endOf(unitOfTime)
          .format('YYYY-MM-DD')
      };
      setSearchDate(params);
      usageAPI.getUserSikdaeUsageDetail(userId, params);
      handleOnOffModal();
    }
  };

  const handlePageChange = (value) => setPage(value);
  const handlePageSizeChange = (_, target) => {
    setPage(1);
    setPageSize(target.value || 25);
  };

  const handleExcelDownload = async () => {
    if (usageTotalHistory.length) {
      setIsDisable(() => true);
      const { thead, tbody } = generateColumns(usageTotalHistory);
      const fileName = `사용자별 식대 사용내역(${dateTypeLabel[dateType.value]})_${moment().valueOf()}`;
      const newCols = [];
      thead.forEach((item) => {
        if (item.header.includes('부서')) {
          newCols.push({ rowNum: 2, value: item.header });
        }
      });
      const newRows = tbody.map(({ row }) => ['', ...row].map(({ data }) => data));
      const apiExcelDatas = {
        0: {
          startRowNum: 3,
          newRows,
          insertColNum: 8,
          newCols
        }
      };
      await handleFileExport(exportedJsonData, apiExcelDatas, fileName);
      setTimeout(() => {
        setIsDisable(() => false);
      }, 1000);
    } else {
      alert(`엑셀 다운로드는 조회된 데이터 기반으로 다운로드됩니다.\n먼저 데이터를 조회해 주세요.`);
    }
  };

  const tableData = useMemo(() => {
    const { totalMealCount, totalComPoint } = usageTotal;
    const { thead, tbody } = generateColumns(usageHistory);

    const sub = [
      { data: '합계', colSpan: thead.length - 2, textAlign: 'left' },
      { data: (totalMealCount || 0).toLocaleString(), textAlign: 'right' },
      { data: (totalComPoint || 0).toLocaleString(), textAlign: 'right' }
    ];
    return { header: { main: thead, sub }, body: { main: tbody, message: '식대 사용내역이 존재하지 않습니다' } };
  }, [usageHistory, usageTotal]);

  return (
    <main className="usgl">
      {isLoading ? <LoadingBar /> : null}
      <div className="top-btn button">
        <Button inverted color="green" disabled={isDisable} onClick={handleExcelDownload}>
          엑셀 다운로드
        </Button>
      </div>
      {/* Search Form */}
      <Form className="top-form" onSubmit={handleSubmit}>
        <Grid.Row className="form-row">
          <Form.Group style={{ alignItems: 'center', margin: '0 0 1em' }}>
            <span style={{ padding: '4px 0px', fontWeight: 700 }}>통계 조건</span>
            <DateType isSum defaultType="SUM" />
            <span style={{ padding: '4px 0px', fontWeight: 700 }}>기간 간편 검색</span>
            <DateForm maxDays={62} />
          </Form.Group>
        </Grid.Row>
        <Grid.Row className="form-row">
          <Form.Group style={{ alignItems: 'center', margin: '0 0 1em' }}>
            <span style={{ padding: '4px 0px', fontWeight: 700 }}>식대 그룹</span>
            <SikdaeGroup style={{ height: '32px' }} />
            <InputWrapper>
              <SearchInputBox
                placeHolder="이름, 사원번호, 로그인ID, 휴대전화번호로 검색하세요."
                styles={{ width: '375px', marginRight: '7px' }}
                handleEntSubmit={(e) => {
                  if (e.charCode === 13) {
                    handleSubmit(e);
                  }
                }}
              />
            </InputWrapper>
            <Button color="black" content="조회" inverted style={{ maxWidth: '100px', height: '32px' }} />
          </Form.Group>
        </Grid.Row>
      </Form>
      {/* Table */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0px' }}>
        <span style={{ fontWeight: 700 }}>검색 결과 : {(usageTotal.totalRow || 0).toLocaleString()}건</span>
        <Dropdown selection defaultValue={25} options={pageSizeOptions} onChange={handlePageSizeChange} />
      </div>
      <div className="table-box">
        <TableForm selectable="true" tabledata={tableData} togglestatus onClick={handleRowClick} />
      </div>
      <div className="table-paging">
        <Pagination
          key={pageSize}
          page={page}
          pageRow={pageSize}
          pageBlock={10}
          totalCount={usageTotal.totalRow}
          pageClick={handlePageChange}
        />
      </div>
      <UserSikdaeUsageDetailModal open={isOpen} close={handleOnOffModal} searchDate={searchDate} />
    </main>
  );
};

export default connect(
  (state) => ({
    date: state.commons.date,
    dateType: state.commons.dateType,
    division: state.commons.division,
    group: state.commons.group,
    keyword: state.commons.text,
    //
    userSikdaeUsageHistory: state.usage.userSikdaeUsageHistory || {},
    userSikdaeUsageTotal: state.usage.userSikdaeUsageTotal || {}
  }),
  (dispatch) => ({
    companyAPI: bindActionCreators(
      {
        sikdaeGroupList: companyActions.SikdaeGroupList
      },
      dispatch
    ),
    usageAPI: bindActionCreators(
      {
        getUserSikdaeUsageHistory: usageAction.UserSikdaeUsageHistory,
        getUserSikdaeUsageTotal: usageAction.UserSikdaeUsageTotal,
        getUserSikdaeUsageDetail: usageAction.UserSikdaeUsageDetail,
        cleanUserSikdaeUsage: usageAction.CleanUserSikdaeUsage
      },
      dispatch
    )
  })
)(UserSikdaeUsageHistory);
